import { useClerk, useUser } from "@clerk/clerk-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { captureException } from "@sentry/react";
import { QRCodeSVG } from "qrcode.react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  KeyIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import LOGO from "../../assets/images/Diligent - 2025 Branding 2.svg";
import {
  updateSponsorUserMfaStatus,
  updateProviderUserMfaStatus,
  updateBackendUserMfaStatus,
} from "../../api/api-methods";
import { clearMfaSetupSessionDeferral } from "../../utils/mfaSetupSessionDeferral";
import { clearAllStickyFilters } from "../../utils/stickyFilterStorage";
import { useHypercomplyLogout } from "../../custom-hooks/queries";

const DEFAULT_DASHBOARD = "/";

function patchMfaStatusForRole(role, payload) {
  const r = role?.toLowerCase?.() ?? "";
  if (r === "sponsor user" || r === "sponsor master user") {
    return updateSponsorUserMfaStatus(payload);
  }
  if (r === "provider user" || r === "provider master user") {
    return updateProviderUserMfaStatus(payload);
  }
  if (r === "backend user" || r === "backend master user") {
    return updateBackendUserMfaStatus(payload);
  }
  return Promise.reject(new Error("Unknown role for MFA status update"));
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-md bg-red-50 p-3" role="alert">
      <div className="flex">
        <div className="shrink-0">
          <svg
            className="h-5 w-5 text-red-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">{message}</p>
        </div>
      </div>
    </div>
  );
}

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg
      className={`animate-spin text-white ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

const primaryButtonClass =
  "inline-flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm";

const secondaryButtonClass =
  "inline-flex w-full items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm";

const inputClass =
  "block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-center font-mono text-lg tracking-[0.2em] text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm";

export default function MfaSetupPage() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const optional = searchParams.get("optional") === "true";

  const [step, setStep] = useState("intro");
  const [totpUri, setTotpUri] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  const role = user?.publicMetadata?.role;

  const { mutate: hyperComplySignout } = useHypercomplyLogout();

  // When the org enforces MFA, MfaEnforcedRoute bounces every other URL back here, so without
  // this control the user is stuck on this screen with no way to sign in as someone else (#14476).
  const handleExit = () => {
    setBusy(true);
    setError("");
    clearMfaSetupSessionDeferral();
    clearAllStickyFilters();

    // Same sign-out path as the sidebar: backend users have no HyperComply session to close.
    if (role === "Backend User") {
      signOut();
      return;
    }

    hyperComplySignout(
      { email: user?.primaryEmailAddress?.emailAddress },
      {
        onSuccess: () => {
          localStorage.setItem("reloadIframe", "true");
          sessionStorage.removeItem("sso_state");
          signOut();
        },
        onError: () => {
          // Getting out matters more than closing the HyperComply session cleanly.
          signOut();
        },
      }
    );
  };

  const invalidateProfileQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ["sponsorUserProfile"] });
    await queryClient.invalidateQueries({ queryKey: ["vendorsMe"] });
    await queryClient.invalidateQueries({ queryKey: ["myProfile"] });
  };

  const handleStart = async () => {
    try {
      setStarting(true);
      setError("");
      const resource = await user?.createTOTP?.();
      if (!resource?.uri || !resource?.secret) {
        setError("Could not start MFA setup. Please try again.");
        return;
      }
      setTotpUri(resource.uri);
      setTotpSecret(resource.secret ?? "");
      setStep("qr");
    } catch (e) {
      // Unlike an invalid TOTP code (expected user error, not reported), failing to
      // even start MFA setup means the Clerk TOTP call itself broke - worth tracking.
      captureException(e);
      const msg =
        e?.errors?.[0]?.message ??
        "Failed to initialize MFA setup. Please try again.";
      setError(msg);
    } finally {
      setStarting(false);
    }
  };

  const handleVerify = async () => {
    try {
      setBusy(true);
      setError("");
      await user?.verifyTOTP?.({ code });
      const bc = await user?.createBackupCode?.();
      const codes = bc?.codes ?? [];
      setBackupCodes(Array.isArray(codes) ? codes : []);
      setStep("backup");
    } catch (e) {
      const msg =
        e?.errors?.[0]?.message ??
        "Invalid code. Please check your authenticator app and try again.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDone = async () => {
    setBusy(true);
    try {
      await patchMfaStatusForRole(role, {
        two_factor_enabled: true,
        mfa_prompt_seen: true,
      });
      clearMfaSetupSessionDeferral();
      await user?.reload?.();
      await invalidateProfileQueries();
      navigate(DEFAULT_DASHBOARD, { replace: true });
    } catch (e) {
      setError(
        e?.response?.data?.message ??
          e?.message ??
          "Could not save MFA status. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!optional) return;
    setBusy(true);
    try {
      setError("");
      await patchMfaStatusForRole(role, {
        mfa_prompt_seen: true,
        two_factor_enabled: false,
      });
      clearMfaSetupSessionDeferral();
      await user?.reload?.();
      await invalidateProfileQueries();
      navigate(DEFAULT_DASHBOARD, { replace: true });
    } catch (e) {
      setError(
        e?.response?.data?.message ??
          e?.message ??
          "Could not update your preferences. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const stepMeta = {
    intro: {
      title: "Set up two-factor authentication",
      subtitle:
        "Add an extra layer of security. You will use an authenticator app such as Google Authenticator or Authy.",
      icon: ShieldCheckIcon,
    },
    qr: {
      title: "Scan the QR code",
      subtitle:
        "Open your authenticator app and scan the code below, or enter the secret manually.",
      icon: DevicePhoneMobileIcon,
    },
    verify: {
      title: "Enter verification code",
      subtitle: "Type the 6-digit code shown in your authenticator app.",
      icon: KeyIcon,
    },
    backup: {
      title: "Save your backup codes",
      subtitle:
        "Store these codes somewhere safe. Each one can only be used once if you lose access to your authenticator app.",
      icon: ClipboardDocumentListIcon,
    },
  };

  const meta = stepMeta[step] ?? stepMeta.intro;
  const Icon = meta.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <div
        className={`w-full overflow-hidden rounded-lg bg-white shadow-xl ${
          step === "backup" ? "max-w-lg" : "max-w-md"
        }`}
      >
        <div className="border-b border-gray-100 px-6 pb-5 pt-8 text-center">
          <div className="mb-5 flex justify-center">
            <img
              src={LOGO}
              className="h-auto w-1/2 max-w-[200px]"
              alt="Diligent"
            />
          </div>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
            <Icon className="h-6 w-6 text-indigo-600" aria-hidden />
          </div>
          <h1 className="text-lg font-bold text-gray-900">{meta.title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            {meta.subtitle}
          </p>
        </div>

        <div className="px-6 py-6">
          {step === "intro" && (
            <div className="space-y-5">
              <ErrorBanner message={error} />
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={busy || starting}
                  className={`${primaryButtonClass} sm:flex-1`}
                >
                  {starting ? (
                    <>
                      <Spinner />
                      Starting…
                    </>
                  ) : (
                    "Get started"
                  )}
                </button>
                {optional && (
                  <button
                    type="button"
                    onClick={handleSkip}
                    disabled={busy || starting}
                    className={`${secondaryButtonClass} sm:flex-1`}
                  >
                    Skip for now
                  </button>
                )}
              </div>
            </div>
          )}

          {step === "qr" && totpUri && (
            <div className="space-y-6">
              <div className="flex justify-center rounded-lg border border-gray-200 bg-gray-50 p-6">
                <QRCodeSVG value={totpUri} size={200} level="M" />
              </div>
              <div className="text-center rounded-md bg-amber-50 p-4 ring-1 ring-inset ring-amber-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Can&apos;t scan?
                </p>
                <p className="mt-1 break-normal text-sm text-amber-900">
                  Enter this key manually:{" "}
                  <code className="rounded bg-white/80 px-1.5 py-0.5 font-mono text-xs text-gray-900 ring-1 ring-amber-200">
                    {totpSecret}
                  </code>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep("verify")}
                className={primaryButtonClass}
              >
                Continue
              </button>
            </div>
          )}

          {step === "verify" && (
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="mfa-totp-code"
                  className="block text-sm font-medium text-gray-700"
                >
                  6-digit code
                </label>
                <input
                  id="mfa-totp-code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={`${inputClass} mt-1`}
                />
              </div>
              <ErrorBanner message={error} />
              <button
                type="button"
                onClick={handleVerify}
                disabled={busy || code.length !== 6}
                className={primaryButtonClass}
              >
                {busy ? (
                  <>
                    <Spinner />
                    Verifying…
                  </>
                ) : (
                  "Verify"
                )}
              </button>
            </div>
          )}

          {step === "backup" && (
            <div className="space-y-5">
              {backupCodes.length > 0 ? (
                <ul className="grid gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
                  {backupCodes.map((c) => (
                    <li
                      key={c}
                      className="rounded-md bg-white px-3 py-2 font-mono text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  No backup codes were returned. You can still continue; contact
                  support if you need recovery codes.
                </p>
              )}
              <ErrorBanner message={error} />
              <button
                type="button"
                onClick={handleDone}
                disabled={busy}
                className={primaryButtonClass}
              >
                {busy ? (
                  <>
                    <Spinner />
                    Saving…
                  </>
                ) : (
                  "I've saved these — continue"
                )}
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleExit}
              disabled={busy || starting}
              data-testid="mfa-setup-exit"
              className="text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            >
              Sign out and use a different account
            </button>
          </div>
          <p className="mt-3 text-center text-xs text-gray-500">
            Two-factor authentication protects your account even if your password
            is compromised.
          </p>
        </div>
      </div>
    </div>
  );
}
